import { useTranslation } from 'react-i18next';

import { VolumeResourceCard } from '../../components/volumeResourceCard';
import { StorageItems } from './components/StorageItems';
import styles from './index.module.less';
import { normalizeContainerVolumeList } from './utils';
import type { VolumeItem } from './volumeTypes';

/** 详情接口返回的卷运行状态，用于直接展示已分配存储信息。 */
export interface ContainerVolumeStatus {
  type: string;
  name: string;
  size?: string;
  mountPath?: string;
  storageClass?: {
    name?: string;
    alias?: string;
    /** 存储驱动标识，详情按“厂商”展示。 */
    provisioner?: string;
  };
  persistentVolumeClaim?: {
    name?: string;
    reclaimPolicy?: string;
    accessModes?: string[];
  };
  phase?: string;
}

interface ContainerStoragePreviewSectionProps {
  volumes?: VolumeItem[] | null;
  /** 传入时直接展示运行状态，不读取表单卷或额外查询 PVC。 */
  volumeStatus?: ContainerVolumeStatus[];
  /** 只读预览下空列表是否隐藏整块，默认 true */
  hideEmptyList?: boolean;
  namespace?: string;
  /** 是否为挂载的已有 PVC 查询并展示容量。 */
  showMountedVolumeCapacity?: boolean;
}

/** 确认页/详情：存储配置完整卡片（无添加/编辑/删除） */
export function ContainerStoragePreviewSection({
  volumes,
  volumeStatus,
  hideEmptyList = true,
  namespace,
  showMountedVolumeCapacity = false,
}: ContainerStoragePreviewSectionProps) {
  const { t } = useTranslation();
  if (volumeStatus) {
    if (!volumeStatus.length && hideEmptyList) return null;

    // 运行状态已包含容量与 PVC 信息，沿用卷卡片展示，保留接口容量单位和全部访问模式。
    return (
      <div className={styles.volumeList}>
        {volumeStatus.map((volume, index) => (
          <VolumeResourceCard
            key={`${volume.name}-${index}`}
            value={volume.name}
            label={`${t('common:storage.class')}: ${volume.storageClass?.alias || volume.storageClass?.name || '-'}`}
            iconfontName="icon-chijiujuanshengming"
            labelStyle={{ display: 'grid', alignItems: 'start', gridTemplateColumns: '36px minmax(0, 2fr) repeat(5, minmax(110px, 1fr))' }}
            extraItems={[
              { label: t('common:manufacturer'), value: volume.storageClass?.provisioner || '-' },
              { label: t('common:capacity'), value: volume.size || '-' },
              { label: t('common:mount.path'), value: volume.mountPath || '-' },
              {
                label: t('common:access.mode'),
                // 逐行展示完整访问模式，保持相邻卷的字段列对齐。
                value: volume.persistentVolumeClaim?.accessModes?.length
                  ? volume.persistentVolumeClaim.accessModes.map((mode) => (
                      <span key={mode} style={{ display: 'block', whiteSpace: 'nowrap' }}>
                        {mode}
                      </span>
                    ))
                  : '-',
              },
              { label: t('common:reclaim.policy'), value: volume.persistentVolumeClaim?.reclaimPolicy || '-' },
            ]}
            canEdit={false}
            canDelete={false}
          />
        ))}
      </div>
    );
  }

  const items = normalizeContainerVolumeList(volumes);
  if (!items.length && hideEmptyList) {
    return null;
  }

  return <StorageItems items={items} mode="readonly" namespace={namespace} showMountedVolumeCapacity={showMountedVolumeCapacity} />;
}
